import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import profileImage from './media/images/img.png';

const NAV_OFFSET = 96;
const LINKS = [
  { id: 'founder', path: '/founder' },
  { id: 'about', hash: 'about' },
  { id: 'services', hash: 'services' },
  { id: 'idea', hash: 'idea' },
  { id: 'partners', hash: 'joining-benefits' },
  { id: 'joinUs', hash: 'registration' },
  { id: 'contact', hash: 'contact' },
  { id: 'aiAssistant', path: '/ai-assistant' },
];

/** Map DOM section order → which nav id should be highlighted (registration uses partners). */
const SCROLL_SECTIONS = [
  ['about', 'about'],
  ['services', 'services'],
  ['idea', 'idea'],
  ['joining-benefits', 'partners'],
  ['registration', 'joinUs'],
  ['contact', 'contact'],
];

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function sectionTop(id) {
  const el = document.getElementById(id);
  if (!el) return Infinity;
  return el.getBoundingClientRect().top + window.scrollY;
}

function getActiveNavFromScroll() {
  const y = window.scrollY + NAV_OFFSET;
  const aboutTop = sectionTop('about');
  if (y < aboutTop - 48) {
    return null;
  }

  for (let i = SCROLL_SECTIONS.length - 1; i >= 0; i--) {
    const [sectionId, navId] = SCROLL_SECTIONS[i];
    const top = sectionTop(sectionId);
    if (y >= top - 24) {
      return navId;
    }
  }
  return 'services';
}

export function NavBar({ t, language, toggleLanguage }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const [activeNav, setActiveNav] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const trackRef = useRef(null);
  const linkRefs = useRef([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, visible: false });

  const measureIndicator = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const idx = LINKS.findIndex((l) => l.id === activeNav);
    if (idx < 0 || activeNav == null) {
      setIndicator((prev) => ({ ...prev, width: 0, visible: false }));
      return;
    }

    const link = linkRefs.current[idx];
    if (!link) return;

    // Physical pixel offsets from the track's border box (stable in both LTR and RTL; avoid
    // transform-only positioning which mis-resolves under dir="rtl").
    const tRect = track.getBoundingClientRect();
    const lRect = link.getBoundingClientRect();
    const left = lRect.left - tRect.left;
    setIndicator({
      left,
      width: lRect.width,
      visible: true,
    });
  }, [activeNav]);

  useLayoutEffect(() => {
    measureIndicator();
  }, [measureIndicator, language, activeNav]);

  useEffect(() => {
    const ro = new ResizeObserver(() => measureIndicator());
    if (trackRef.current) ro.observe(trackRef.current);
    window.addEventListener('resize', measureIndicator);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureIndicator);
    };
  }, [measureIndicator]);

  useEffect(() => {
    const onScroll = () => {
      setActiveNav(getActiveNavFromScroll());
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, language]);

  const scrollToId = useCallback((hash) => {
    const el = document.getElementById(hash);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({
      top: y,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
    setActiveNav(
      hash === 'registration' ? 'joinUs' : hash === 'joining-benefits' ? 'partners' : hash
    );
  }, []);

  const onNavClick = (e, link) => {
    if (link.path) {
      e.preventDefault();
      navigate(link.path);
      setMobileOpen(false);
      return;
    }
    if (!isHome) {
      e.preventDefault();
      navigate(`/#${link.hash}`);
      return;
    }

    e.preventDefault();
    scrollToId(link.hash);
    setMobileOpen(false);
  };

  return (
    <nav className="fixed top-0 w-full z-50 bg-[#000c1e]/90 backdrop-blur-xl dark:bg-[#000c1e]/90 shadow-[0_32px_32px_-4px_rgba(0,12,30,0.06)] border-b-2 border-secondary/20">
      <div className="flex justify-between items-center px-4 sm:px-8 py-3 sm:py-4 max-w-screen-2xl mx-auto font-['Plus_Jakarta_Sans'] tracking-tight gap-3">
        <button
          type="button"
          onClick={() => {
            if (!isHome) {
              navigate('/');
              return;
            }
            window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
            setActiveNav(null);
          }}
          className="flex items-center gap-2 sm:gap-3 text-lg sm:text-2xl font-bold tracking-tighter text-white shrink-0 bg-transparent border-none cursor-pointer p-0 font-[inherit] text-start"
        >
          <img
            src={profileImage}
            alt=""
            aria-hidden="true"
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border border-white/20"
          />
          <span>
            holol Club <span className="text-secondary">.</span>
          </span>
        </button>

        <div
          ref={trackRef}
          className={`hidden md:flex relative items-center gap-8 ${language === 'ar' ? 'md:flex-row-reverse' : ''}`}
          dir="ltr"
        >
          {LINKS.map((link, i) => {
            const isActive = activeNav === link.id;
            return (
              <a
                key={link.id}
                ref={(el) => {
                  linkRefs.current[i] = el;
                }}
                href={link.path ? link.path : `/#${link.hash}`}
                onClick={(e) => onNavClick(e, link)}
                dir="auto"
                className={`relative z-10 pb-1 transition-colors duration-300 ${
                  isActive ? 'text-[#775a19] font-bold' : 'text-white/80 hover:text-white font-medium'
                }`}
              >
                {t(`nav.${link.id}`)}
              </a>
            );
          })}
          <span
            className="pointer-events-none absolute bottom-0 left-0 h-0.5 rounded-full bg-[#775a19] shadow-[0_0_12px_rgba(119,90,25,0.5)] transition-[left,width,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
            style={{
              left: indicator.visible ? indicator.left : 0,
              width: indicator.visible ? indicator.width : 0,
              opacity: indicator.visible ? 1 : 0,
              right: 'auto',
            }}
            aria-hidden="true"
          />
        </div>

        <div className="flex items-center gap-3 sm:gap-6 shrink-0">
          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-white/15 bg-white/5 text-white hover:bg-white/10 transition-colors"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              {mobileOpen ? 'close' : 'menu'}
            </span>
          </button>
          <button
            type="button"
            onClick={toggleLanguage}
            className="text-white/80 hover:text-white transition-colors text-sm font-medium"
            lang={language === 'en' ? 'ar' : 'en'}
          >
            {t('nav.switchLang')}
          </button>
          <button
            type="button"
            onClick={() => {
              if (!isHome) {
                navigate('/#registration');
                return;
              }
              scrollToId('registration');
            }}
            className="hidden sm:inline-flex bg-secondary text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-tertiary transition-all duration-300 active:scale-95"
          >
            {t('nav.joinPartner')}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden border-t border-white/10 bg-[#000c1e]/95 backdrop-blur-xl ${
          mobileOpen ? 'block' : 'hidden'
        }`}
      >
        <div className="px-4 sm:px-8 py-4 max-w-screen-2xl mx-auto space-y-2">
          {LINKS.map((link) => (
            <a
              key={link.id}
              href={link.path ? link.path : `/#${link.hash}`}
              onClick={(e) => onNavClick(e, link)}
              dir="auto"
              className="block w-full rounded-xl px-4 py-3 text-white/90 hover:text-white hover:bg-white/10 transition-colors font-semibold"
            >
              {t(`nav.${link.id}`)}
            </a>
          ))}
          <button
            type="button"
            onClick={() => {
              toggleLanguage();
              setMobileOpen(false);
            }}
            className="w-full rounded-xl px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 transition-colors font-semibold text-start"
            lang={language === 'en' ? 'ar' : 'en'}
          >
            {t('nav.switchLang')}
          </button>
        </div>
      </div>
    </nav>
  );
}
